import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useAuth, API } from '../contexts/AuthContext';

// Modulo Parceiros — portal de parceiros do Centrao.
// A lista de parceiros vem direto do BDENTER (categoria_r WHERE rsuper = 2128) —
// nao se cadastra parceiro. A cada parceiro o Marketing vincula produtos do catalogo.

const ROXO = '#6B2C5C';

export default function Parceiros() {
  const { temPermissao } = useAuth();
  const podeGerenciar = temPermissao('parceiros_gerenciar');

  const [parceiros, setParceiros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState('');

  const [parceiroAlvo, setParceiroAlvo] = useState(null); // {oid, nome, qtd_produtos}

  const erro = (msg) => Swal.fire({ icon: 'error', title: 'Ops', text: msg, confirmButtonColor: ROXO });

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/parceiros`)
      .then((r) => setParceiros(r.data))
      .catch((e) => { erro(e.response?.data?.error || 'Nao foi possivel carregar a lista de parceiros.'); setParceiros([]); })
      .finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  if (parceiroAlvo) {
    return (
      <VinculosProduto
        parceiro={parceiroAlvo}
        podeGerenciar={podeGerenciar}
        onVoltar={() => { setParceiroAlvo(null); carregar(); }}
      />
    );
  }

  const lista = filtro.trim()
    ? parceiros.filter(p => p.nome.toLowerCase().includes(filtro.toLowerCase()))
    : parceiros;

  return (
    <div className="container-fluid py-3" style={{ maxWidth: 1100 }}>
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h3 className="mb-0" style={{ color: ROXO }}>
          <i className="bi bi-people-fill me-2" />Parceiros
        </h3>
        <div className="d-flex align-items-center gap-2">
          <input
            className="form-control form-control-sm"
            style={{ minWidth: 220 }}
            placeholder="Buscar parceiro…"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </div>
      </div>

      <div className="alert alert-light border small mb-3">
        <i className="bi bi-info-circle me-1" />
        A lista de parceiros vem direto do ERP (categorias). Clique num parceiro para vincular produtos.
      </div>

      {carregando && <div className="text-center py-5"><div className="spinner-border" style={{ color: ROXO }} /></div>}

      {!carregando && !lista.length && (
        <div className="text-center text-muted py-5">
          <i className="bi bi-people fs-1 d-block mb-2" />
          {filtro ? 'Nenhum parceiro com esse nome.' : 'Nenhum parceiro encontrado no ERP.'}
        </div>
      )}

      {!carregando && lista.length > 0 && (
        <div className="row g-3">
          {lista.map((p) => (
            <div key={p.oid} className="col-md-6 col-lg-4">
              <button
                className="card h-100 w-100 text-start border-0 shadow-sm"
                style={{ background: '#fff', cursor: 'pointer', transition: 'transform .15s' }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                onClick={() => setParceiroAlvo(p)}
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h5 className="mb-1" style={{ color: ROXO }}>{p.nome}</h5>
                      <div className="text-muted small">
                        <i className="bi bi-box-seam me-1" />
                        {p.qtd_produtos} produto{p.qtd_produtos === 1 ? '' : 's'} vinculado{p.qtd_produtos === 1 ? '' : 's'}
                      </div>
                    </div>
                    <i className="bi bi-arrow-right-circle fs-4" style={{ color: ROXO }} />
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tela de vinculos de produtos de um parceiro (categoria_oid)
function VinculosProduto({ parceiro, podeGerenciar, onVoltar }) {
  const [vinculos, setVinculos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [marcados, setMarcados] = useState({}); // { [vinculoId]: true }

  const erro = (msg) => Swal.fire({ icon: 'error', title: 'Ops', text: msg, confirmButtonColor: ROXO });
  const ok = (msg) => Swal.fire({ icon: 'success', title: 'Pronto', text: msg, timer: 1500, showConfirmButton: false });

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/parceiros/${encodeURIComponent(parceiro.oid)}/produtos`)
      .then((r) => { setVinculos(r.data); setMarcados({}); })
      .catch(() => setVinculos([]))
      .finally(() => setCarregando(false));
  }, [parceiro.oid]);
  useEffect(() => { carregar(); }, [carregar]);

  async function desvincular(v) {
    const c = await Swal.fire({
      icon: 'warning', title: 'Desvincular produto?', text: `${v.codpro} — ${v.descricao}`,
      showCancelButton: true, confirmButtonText: 'Desvincular', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33', cancelButtonColor: '#888',
    });
    if (!c.isConfirmed) return;
    try {
      await axios.delete(`${API}/parceiros/${encodeURIComponent(parceiro.oid)}/produtos/${v.id}`);
      ok('Desvinculado.');
      carregar();
    } catch (e) { erro(e.response?.data?.error || 'Nao foi possivel desvincular.'); }
  }

  function toggle(id) {
    setMarcados((prev) => {
      const novo = { ...prev };
      if (novo[id]) delete novo[id];
      else novo[id] = true;
      return novo;
    });
  }
  function toggleTodos() {
    const todos = vinculos.every(v => marcados[v.id]);
    if (todos) setMarcados({});
    else {
      const m = {};
      vinculos.forEach(v => { m[v.id] = true; });
      setMarcados(m);
    }
  }

  async function desvincularLote() {
    const ids = Object.keys(marcados).map(Number);
    if (!ids.length) return;
    const c = await Swal.fire({
      icon: 'warning', title: 'Desvincular selecionados?',
      text: `${ids.length} produto(s) serao removidos do parceiro "${parceiro.nome}".`,
      showCancelButton: true, confirmButtonText: 'Desvincular', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33', cancelButtonColor: '#888',
    });
    if (!c.isConfirmed) return;
    try {
      const r = await axios.post(`${API}/parceiros/${encodeURIComponent(parceiro.oid)}/produtos/desvincular-lote`, { vinculo_ids: ids });
      ok(`${r.data.removidos} produto(s) desvinculado(s).`);
      carregar();
    } catch (e) { erro(e.response?.data?.error || 'Nao foi possivel desvincular em lote.'); }
  }

  const totalMarcados = Object.keys(marcados).length;
  const todosMarcados = vinculos.length > 0 && vinculos.every(v => marcados[v.id]);

  return (
    <div className="container-fluid py-3" style={{ maxWidth: 1200 }}>
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <button className="btn btn-sm btn-outline-secondary mb-2" onClick={onVoltar}><i className="bi bi-arrow-left me-1" />Voltar</button>
          <h3 className="mb-0" style={{ color: ROXO }}>
            <i className="bi bi-box-seam me-2" />{parceiro.nome}
          </h3>
          <div className="text-muted small mt-1">Produtos vinculados a este parceiro</div>
        </div>
        {podeGerenciar && (
          <div className="d-flex gap-2">
            {totalMarcados > 0 && (
              <button className="btn btn-outline-danger" onClick={desvincularLote}>
                <i className="bi bi-x-lg me-1" />Desvincular ({totalMarcados})
              </button>
            )}
            <button className="btn" style={{ background: ROXO, color: '#fff' }} onClick={() => setBuscaAberta(true)}>
              <i className="bi bi-plus-lg me-1" />Vincular produtos
            </button>
          </div>
        )}
      </div>

      {carregando && <div className="text-center py-5"><div className="spinner-border" style={{ color: ROXO }} /></div>}

      {!carregando && !vinculos.length && (
        <div className="text-center text-muted py-5">
          <i className="bi bi-box fs-1 d-block mb-2" />
          Nenhum produto vinculado.{podeGerenciar ? ' Clique em "Vincular produtos" para buscar no catalogo.' : ''}
        </div>
      )}

      {!carregando && vinculos.length > 0 && (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr className="text-muted small">
                {podeGerenciar && (
                  <th style={{ width: 32 }}>
                    <input type="checkbox" className="form-check-input" checked={todosMarcados} onChange={toggleTodos} title="Selecionar todos" />
                  </th>
                )}
                <th>Codigo</th>
                <th>Descricao</th>
                <th>Classe</th>
                <th className="text-end">Preco venda</th>
                {podeGerenciar && <th className="text-end">Acoes</th>}
              </tr>
            </thead>
            <tbody>
              {vinculos.map((v) => (
                <tr key={v.id} className={marcados[v.id] ? 'table-active' : ''}>
                  {podeGerenciar && (
                    <td>
                      <input type="checkbox" className="form-check-input" checked={!!marcados[v.id]} onChange={() => toggle(v.id)} />
                    </td>
                  )}
                  <td><code>{v.codpro}</code></td>
                  <td>{v.descricao}</td>
                  <td className="small text-muted">{v.classe_nome || v.classe || '—'}</td>
                  <td className="text-end">{v.preco_venda ? `R$ ${v.preco_venda.toFixed(2)}` : '—'}</td>
                  {podeGerenciar && (
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-danger" onClick={() => desvincular(v)} title="Desvincular">
                        <i className="bi bi-x-lg" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-muted small">{vinculos.length} produto(s) vinculado(s){totalMarcados > 0 ? ` — ${totalMarcados} selecionado(s)` : ''}</div>
        </div>
      )}

      {buscaAberta && (
        <BuscaProdutosModal
          parceiro={parceiro}
          onFechar={() => setBuscaAberta(false)}
          onVinculado={() => { carregar(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal de busca/vinculo de produtos no BDENTER.
// Filtros: codpro, descricao, multi-select de classes (regra 12 + regra 13).
function BuscaProdutosModal({ parceiro, onFechar, onVinculado }) {
  const [classes, setClasses] = useState([]);
  const [classesSelecionadas, setClassesSelecionadas] = useState([]); // [{clasprod, descricao}]
  const [classeInput, setClasseInput] = useState('');
  const [classeDropdownAberto, setClasseDropdownAberto] = useState(false);

  const [codpro, setCodpro] = useState('');
  const [descricao, setDescricao] = useState('');
  const [emPromocao, setEmPromocao] = useState(false);

  const [resultados, setResultados] = useState([]);
  const [selecionados, setSelecionados] = useState({}); // { [codpro]: produto }
  const [buscando, setBuscando] = useState(false);
  const [vinculando, setVinculando] = useState(false);

  const classeBoxRef = useRef(null);

  const erro = (msg) => Swal.fire({ icon: 'error', title: 'Ops', text: msg, confirmButtonColor: ROXO });
  const ok = (msg) => Swal.fire({ icon: 'success', title: 'Pronto', text: msg, timer: 1500, showConfirmButton: false });

  useEffect(() => {
    axios.get(`${API}/parceiros/classes`).then((r) => setClasses(r.data)).catch(() => setClasses([]));
  }, []);

  // Regra 33 — dropdown custom: fechar no ESC e click-outside via mousedown
  useEffect(() => {
    function onMouseDown(ev) {
      if (classeBoxRef.current && !classeBoxRef.current.contains(ev.target)) setClasseDropdownAberto(false);
    }
    function onKey(ev) { if (ev.key === 'Escape') setClasseDropdownAberto(false); }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const classesFiltradas = classes.filter((c) => {
    if (classesSelecionadas.find((s) => s.clasprod === c.clasprod)) return false;
    if (!classeInput.trim()) return true;
    const q = classeInput.toLowerCase();
    return c.descricao.toLowerCase().includes(q) || c.clasprod.toLowerCase().includes(q);
  }).slice(0, 50);

  function addClasse(c) {
    setClassesSelecionadas((prev) => [...prev, c]);
    setClasseInput('');
  }
  function rmClasse(c) {
    setClassesSelecionadas((prev) => prev.filter((x) => x.clasprod !== c.clasprod));
  }

  async function buscar() {
    if (!codpro.trim() && !descricao.trim() && !classesSelecionadas.length && !emPromocao) {
      return erro('Informe ao menos um filtro (codigo, descricao, classe ou em promocao).');
    }
    setBuscando(true);
    setSelecionados({});
    try {
      const params = { categoria_oid: parceiro.oid };
      if (codpro.trim()) params.codpro = codpro.trim();
      if (descricao.trim()) params.descricao = descricao.trim();
      if (classesSelecionadas.length) params.classes = classesSelecionadas.map((c) => c.clasprod).join(',');
      if (emPromocao) params.em_promocao = 'true';
      const r = await axios.get(`${API}/parceiros/produtos-bdenter`, { params });
      setResultados(r.data);
      if (!r.data.length) {
        Swal.fire({ icon: 'info', title: 'Nada encontrado', text: 'Ajuste os filtros e tente novamente.', confirmButtonColor: ROXO });
      }
    } catch (e) {
      erro(e.response?.data?.error || 'Nao foi possivel buscar produtos.');
    } finally { setBuscando(false); }
  }

  function toggleProduto(p) {
    if (p.vinculado) return;
    setSelecionados((prev) => {
      const novo = { ...prev };
      if (novo[p.codpro]) delete novo[p.codpro];
      else novo[p.codpro] = p;
      return novo;
    });
  }
  function toggleTodos() {
    const disponiveis = resultados.filter((p) => !p.vinculado);
    const todosMarcados = disponiveis.every((p) => selecionados[p.codpro]);
    if (todosMarcados) setSelecionados({});
    else {
      const m = {};
      disponiveis.forEach((p) => { m[p.codpro] = p; });
      setSelecionados(m);
    }
  }

  async function vincular() {
    const lista = Object.values(selecionados);
    if (!lista.length) return erro('Selecione ao menos um produto.');
    setVinculando(true);
    try {
      // Preco/custo nao sao enviados — o backend le do BDENTER em tempo real.
      const corpo = {
        categoria_nome: parceiro.nome,
        produtos: lista.map((p) => ({
          codpro: p.codpro, descricao: p.descricao,
          classe: p.clasprod, classe_nome: p.classe_nome,
        })),
      };
      const r = await axios.post(`${API}/parceiros/${encodeURIComponent(parceiro.oid)}/produtos`, corpo);
      ok(`${r.data.inseridos} produto(s) vinculado(s).`);
      onVinculado();
      onFechar();
    } catch (e) {
      erro(e.response?.data?.error || 'Nao foi possivel vincular.');
    } finally { setVinculando(false); }
  }

  const totalSelecionados = Object.keys(selecionados).length;
  const disponiveis = resultados.filter((p) => !p.vinculado);
  const todosMarcados = disponiveis.length > 0 && disponiveis.every((p) => selecionados[p.codpro]);

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3 p-4 mt-3" style={{ maxWidth: 980, width: '100%' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0" style={{ color: ROXO }}><i className="bi bi-search me-2" />Buscar produtos no catalogo</h5>
          <button className="btn-close" onClick={onFechar} />
        </div>

        <div className="row g-2 mb-2">
          <div className="col-md-3">
            <label className="form-label small fw-bold">Codigo</label>
            <input className="form-control form-control-sm" value={codpro} onChange={(e) => setCodpro(e.target.value)} placeholder="Ex: 1234" onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }} />
          </div>
          <div className="col-md-4">
            <label className="form-label small fw-bold">Descricao contem</label>
            <input className="form-control form-control-sm" value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex: cimento" onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }} />
          </div>
          <div className="col-md-5" ref={classeBoxRef} style={{ position: 'relative' }}>
            <label className="form-label small fw-bold">Classes</label>
            <input
              className="form-control form-control-sm"
              value={classeInput}
              onChange={(e) => { setClasseInput(e.target.value); setClasseDropdownAberto(true); }}
              onFocus={() => setClasseDropdownAberto(true)}
              onClick={() => setClasseDropdownAberto(true)}
              placeholder="Clique ou digite para escolher classes"
            />
            {classeDropdownAberto && classesFiltradas.length > 0 && (
              <div style={{ position: 'fixed', zIndex: 1100, marginTop: 2, background: '#fff', border: '1px solid #ccc', borderRadius: 4, maxHeight: 240, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,.15)', minWidth: 260 }}>
                {classesFiltradas.map((c) => (
                  <button
                    key={c.clasprod} type="button"
                    className="btn btn-sm w-100 text-start px-2 py-1"
                    style={{ borderRadius: 0, background: 'transparent' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#F5EBF2'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    onClick={() => addClasse(c)}
                  >
                    <span className="text-muted small me-2">{c.clasprod}</span>{c.descricao}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {classesSelecionadas.length > 0 && (
          <div className="mb-2 d-flex flex-wrap gap-1 align-items-center">
            {classesSelecionadas.map((c) => (
              <span key={c.clasprod} className="badge" style={{ background: '#F5EBF2', color: ROXO, border: `1px solid ${ROXO}` }}>
                {c.descricao}
                <button type="button" className="btn-close ms-2" style={{ fontSize: '.55em' }} onClick={() => rmClasse(c)} aria-label="remover" />
              </span>
            ))}
            <button type="button" className="btn btn-sm btn-link text-decoration-none p-0 ms-1" onClick={() => setClassesSelecionadas([])}>limpar</button>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="form-check">
            <input
              className="form-check-input" type="checkbox" id="filtro-promo"
              checked={emPromocao} onChange={(e) => setEmPromocao(e.target.checked)}
              style={emPromocao ? { background: ROXO, borderColor: ROXO } : {}}
            />
            <label className="form-check-label small" htmlFor="filtro-promo">
              <i className="bi bi-tag-fill me-1" style={{ color: ROXO }} />
              Apenas produtos em promocao <span className="text-muted">(em alguma campanha ativa)</span>
            </label>
          </div>
          <button className="btn btn-sm btn-outline-secondary" disabled={buscando} onClick={buscar}>
            {buscando ? <><span className="spinner-border spinner-border-sm me-1" />Buscando…</> : <><i className="bi bi-search me-1" />Buscar</>}
          </button>
        </div>

        {resultados.length > 0 && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="small text-muted">
                {resultados.length} produto(s) encontrado(s){disponiveis.length < resultados.length ? ` — ${resultados.length - disponiveis.length} ja vinculado(s)` : ''}.
              </div>
              <button className="btn btn-sm btn-link text-decoration-none" onClick={toggleTodos} disabled={!disponiveis.length}>
                {todosMarcados ? 'Desmarcar todos' : 'Selecionar todos'}
              </button>
            </div>
            <div className="table-responsive" style={{ maxHeight: 400, overflowY: 'auto' }}>
              <table className="table table-sm align-middle">
                <thead>
                  <tr className="text-muted small">
                    <th style={{ width: 30 }}></th>
                    <th>Codigo</th>
                    <th>Descricao</th>
                    <th>Classe</th>
                    <th className="text-end">Preco</th>
                  </tr>
                </thead>
                <tbody>
                  {resultados.map((p) => (
                    <tr key={p.codpro} className={p.vinculado ? 'text-muted' : ''} style={{ cursor: p.vinculado ? 'not-allowed' : 'pointer' }} onClick={() => toggleProduto(p)}>
                      <td>
                        {p.vinculado
                          ? <i className="bi bi-check2-circle" title="Ja vinculado" />
                          : <input type="checkbox" className="form-check-input" checked={!!selecionados[p.codpro]} onChange={() => toggleProduto(p)} onClick={(e) => e.stopPropagation()} />}
                      </td>
                      <td>
                        <code>{p.codpro}</code>
                        {p.em_promocao && (
                          <span className="badge ms-2" style={{ background: ROXO, color: '#fff', fontSize: '.65em' }} title="Esta em alguma campanha ativa">
                            <i className="bi bi-tag-fill me-1" />promo
                          </span>
                        )}
                      </td>
                      <td>{p.descricao}</td>
                      <td className="small text-muted">{p.classe_nome}</td>
                      <td className="text-end">R$ {(p.preco_venda || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted small">{totalSelecionados} selecionado(s)</span>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary" onClick={onFechar}>Fechar</button>
            <button className="btn" style={{ background: ROXO, color: '#fff' }} disabled={vinculando || !totalSelecionados} onClick={vincular}>
              {vinculando ? <><span className="spinner-border spinner-border-sm me-1" />Vinculando…</> : <><i className="bi bi-link-45deg me-1" />Vincular {totalSelecionados ? `(${totalSelecionados})` : ''}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
